import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { ArrowLeft, Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import { ErrorBanner, StatusBadge } from '../components/catalog'
import { Tab, TabCount, Tabs, TabsList, TabsPanel } from '../components/tabs'
import { CountryForm, emptyCountryForm, type CountryFormValue } from '../components/geoForms'
import { BankForm, emptyBankForm, type BankFormValue } from '../components/bankForm'
import { Flag } from '../components/flag'
import type { Country } from './Countries'
import type { Bank } from './Banks'

interface DocumentType {
  documentTypeId: number
  countryId: number
  documentType: string
  documentCode: string
  description: string
  validatorRegex: string
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

const TABS = ['summary', 'documents', 'banks'] as const
type TabValue = (typeof TABS)[number]

const emptyDocumentForm = { documentType: '', documentCode: '', description: '', validatorRegex: '' }

/**
 * CountryDetail is one country and what hangs off it: its own fields, and the
 * identity documents it accepts ([Catalog].[Network.DocumentType]).
 *
 * Document codes repeat across countries — nine of them accept PASS — so they
 * are only unique within this page's country. A document belongs to its country
 * for good: there is no "move" here, and the API refuses one country's document
 * reached through another's URL.
 */
export function CountryDetailPage() {
  const { t } = useTranslation('app')
  const { id } = useParams()
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')

  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab') as TabValue | null
  const tab: TabValue = requested && TABS.includes(requested) ? requested : 'summary'

  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<CountryFormValue>(emptyCountryForm)
  const [docDialogOpen, setDocDialogOpen] = useState(false)
  const [editingDoc, setEditingDoc] = useState<DocumentType | null>(null)
  const [docForm, setDocForm] = useState(emptyDocumentForm)

  const [bankDialogOpen, setBankDialogOpen] = useState(false)
  const [editingBank, setEditingBank] = useState<Bank | null>(null)
  const [bankForm, setBankForm] = useState<BankFormValue>(emptyBankForm)

  const { data: country } = useQuery({
    queryKey: ['countries', id],
    queryFn: async (): Promise<Country> => {
      const res = await api.get(`/countries/${id}`)
      return res.data as Country
    },
  })

  const { data: docData } = useQuery({
    queryKey: ['countries', id, 'document-types'],
    queryFn: async (): Promise<{ documentTypes: DocumentType[] }> => {
      const res = await api.get(`/countries/${id}/document-types`)
      return res.data as { documentTypes: DocumentType[] }
    },
  })

  useEffect(() => {
    if (!country) return
    setForm({
      country: country.country,
      englishShortName: country.englishShortName,
      isoCode2: country.isoCode2,
      isoCode3: country.isoCode3,
      isCompulsoryDocument: country.isCompulsoryDocument,
      numericCode: country.numericCode === null ? '' : String(country.numericCode),
    })
  }, [country])

  // The banks of this country come from the catalogue itself (?countryId=),
  // not from a nested route: a bank is a PSP catalogue entry that happens to
  // belong to a country, and one route family is enough.
  const { data: bankData } = useQuery({
    queryKey: ['banks', { countryId: id, all: true }],
    queryFn: async (): Promise<{ banks: Bank[] }> => {
      const res = await api.get('/banks', {
        params: { countryId: id, includeDisabled: 'true', limit: 200 },
      })
      return res.data as { banks: Bank[] }
    },
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['countries'] })
    qc.invalidateQueries({ queryKey: ['reference'] })
    qc.invalidateQueries({ queryKey: ['banks'] })
  }
  const onError = (err: unknown) => setError(messageOf(err, t('countries.errors.save')))

  const save = useMutation({
    mutationFn: () =>
      api.put(`/countries/${id}`, {
        country: form.country.trim(),
        englishShortName: form.englishShortName.trim(),
        isoCode2: form.isoCode2.trim(),
        isoCode3: form.isoCode3.trim(),
        isCompulsoryDocument: form.isCompulsoryDocument,
        numericCode: form.numericCode === '' ? null : Number(form.numericCode),
        isDisabledByPlatform: country?.isDisabledByPlatform ?? false,
      }),
    onSuccess: () => {
      setEditing(false)
      setError('')
      invalidate()
    },
    onError,
  })

  const toggleDisabled = useMutation({
    mutationFn: () =>
      country?.isDisabledByPlatform
        ? api.post(`/countries/${id}/restore`)
        : api.delete(`/countries/${id}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  const saveDoc = useMutation({
    mutationFn: () => {
      const body = {
        documentType: docForm.documentType.trim(),
        documentCode: docForm.documentCode.trim(),
        description: docForm.description.trim(),
        validatorRegex: docForm.validatorRegex.trim(),
        isDisabledByPlatform: editingDoc?.isDisabledByPlatform ?? false,
      }
      return editingDoc
        ? api.put(`/countries/${id}/document-types/${editingDoc.documentTypeId}`, body)
        : api.post(`/countries/${id}/document-types`, body)
    },
    onSuccess: () => {
      setDocDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('countries.documents.errors.save'))),
  })

  const toggleDoc = useMutation({
    mutationFn: (d: DocumentType) =>
      d.isDisabledByPlatform
        ? api.post(`/countries/${id}/document-types/${d.documentTypeId}/restore`)
        : api.delete(`/countries/${id}/document-types/${d.documentTypeId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('countries.documents.errors.save'))),
  })

  const saveBank = useMutation({
    mutationFn: () => {
      const body = {
        bank: bankForm.bank.trim(),
        swift: bankForm.swift.trim(),
        countryId: Number(id),
        isDisabledByPlatform: editingBank?.isDisabledByPlatform ?? false,
      }
      return editingBank
        ? api.put(`/banks/${editingBank.bankId}`, body)
        : api.post('/banks', body)
    },
    onSuccess: () => {
      setBankDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('banks.errors.save'))),
  })

  const toggleBank = useMutation({
    mutationFn: (b: Bank) =>
      b.isDisabledByPlatform ? api.post(`/banks/${b.bankId}/restore`) : api.delete(`/banks/${b.bankId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('banks.errors.save'))),
  })

  const documents = docData?.documentTypes ?? []
  const banks = bankData?.banks ?? []

  const openCreateBank = () => {
    setEditingBank(null)
    setBankForm({ ...emptyBankForm, countryId: String(id ?? '') })
    setError('')
    setBankDialogOpen(true)
  }

  const openEditBank = (b: Bank) => {
    setEditingBank(b)
    setBankForm({ bank: b.bank, swift: b.swift, countryId: String(b.countryId) })
    setError('')
    setBankDialogOpen(true)
  }

  const openCreateDoc = () => {
    setEditingDoc(null)
    setDocForm(emptyDocumentForm)
    setError('')
    setDocDialogOpen(true)
  }

  const openEditDoc = (d: DocumentType) => {
    setEditingDoc(d)
    setDocForm({
      documentType: d.documentType,
      documentCode: d.documentCode,
      description: d.description,
      validatorRegex: d.validatorRegex,
    })
    setError('')
    setDocDialogOpen(true)
  }

  if (!country) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
  }

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')
  const canSubmitDoc = docForm.documentType.trim() !== '' && docForm.documentCode.trim() !== ''

  return (
    <div>
      <Link
        to="/countries"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('countries.title')}
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-semibold">
            <Flag region={country.isoCode2} className="text-2xl" />
            {country.country}
          </h2>
          <p className="text-sm text-muted-foreground">
            {country.isoCode2}
            {country.isoCode3 ? ` · ${country.isoCode3}` : ''}
            {country.englishShortName ? ` · ${country.englishShortName}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge disabled={country.isDisabledByPlatform} />
          {canWrite && (
            <Button
              variant="outline"
              size="sm"
              disabled={toggleDisabled.isPending}
              onClick={() => toggleDisabled.mutate()}
            >
              {country.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
              {country.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
            </Button>
          )}
        </div>
      </div>

      <ErrorBanner message={error} />

      <Tabs
        value={tab}
        onValueChange={(value) => {
          searchParams.set('tab', String(value))
          setSearchParams(searchParams, { replace: true })
        }}
      >
        <TabsList>
          <Tab value="summary">{t('countries.tabs.summary')}</Tab>
          <Tab value="documents">
            {t('countries.tabs.documents')}
            <TabCount value={documents.length} />
          </Tab>
          <Tab value="banks">
            {t('countries.tabs.banks')}
            <TabCount value={banks.length} />
          </Tab>
        </TabsList>

        <TabsPanel value="summary">
          {editing ? (
            <CountryForm
              value={form}
              onChange={setForm}
              onSubmit={() => save.mutate()}
              onCancel={() => {
                setEditing(false)
                setError('')
              }}
              pending={save.isPending}
              error={error}
              submitLabel={t('common.save')}
            />
          ) : (
            <>
              <Card className="max-w-3xl">
                <CardContent>
                  <dl className="grid grid-cols-[14rem_1fr] gap-x-6 gap-y-3 text-sm">
                    <dt className="text-muted-foreground">{t('countries.columns.id')}</dt>
                    <dd className="font-mono text-xs">{country.countryId}</dd>
                    <dt className="text-muted-foreground">{t('countries.columns.name')}</dt>
                    <dd>{country.country}</dd>
                    <dt className="text-muted-foreground">{t('countries.columns.englishName')}</dt>
                    <dd>{country.englishShortName || '—'}</dd>
                    <dt className="text-muted-foreground">{t('countries.columns.iso2')}</dt>
                    <dd className="font-mono text-xs">{country.isoCode2}</dd>
                    <dt className="text-muted-foreground">{t('countries.columns.iso3')}</dt>
                    <dd className="font-mono text-xs">{country.isoCode3 || '—'}</dd>
                    <dt className="text-muted-foreground">{t('countries.columns.numericCode')}</dt>
                    <dd className="font-mono text-xs">{country.numericCode ?? '—'}</dd>
                    <dt className="text-muted-foreground">
                      {t('countries.columns.compulsoryDocument')}
                    </dt>
                    <dd>{country.isCompulsoryDocument ? t('common.yes') : t('common.no')}</dd>
                    <dt className="text-muted-foreground">{t('common.columns.created')}</dt>
                    <dd>{formatDate(country.creationDate)}</dd>
                    <dt className="text-muted-foreground">
                      {t('paymentMethods.summary.lastModified')}
                    </dt>
                    <dd>{formatDate(country.lastModificationDate)}</dd>
                  </dl>
                </CardContent>
              </Card>

              {canWrite && (
                <Button className="mt-6" variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil />
                  {t('common.edit')}
                </Button>
              )}
            </>
          )}
        </TabsPanel>

        <TabsPanel value="documents">
          <p className="mb-3 text-sm text-muted-foreground">{t('countries.documents.intro')}</p>

          {canWrite && (
            <div className="mb-3 flex justify-end">
              <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
                <DialogTrigger
                  render={
                    <Button size="sm" onClick={openCreateDoc}>
                      <Plus />
                      {t('countries.documents.newDocument')}
                    </Button>
                  }
                />
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editingDoc
                        ? t('countries.documents.editDocument')
                        : t('countries.documents.newDocument')}
                    </DialogTitle>
                  </DialogHeader>
                  {/* The country is this page, so it is not a field: a document
                      belongs to its country for good. */}
                  <form
                    className="flex flex-col gap-4"
                    onSubmit={(e) => {
                      e.preventDefault()
                      if (canSubmitDoc) saveDoc.mutate()
                    }}
                  >
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="doc-name">{t('countries.documents.columns.name')}</Label>
                      <Input
                        id="doc-name"
                        value={docForm.documentType}
                        onChange={(e) => setDocForm({ ...docForm, documentType: e.target.value })}
                        maxLength={100}
                        placeholder="Documento Nacional de Identidad"
                        autoFocus
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="doc-code">{t('countries.documents.columns.code')}</Label>
                      <Input
                        id="doc-code"
                        value={docForm.documentCode}
                        onChange={(e) => setDocForm({ ...docForm, documentCode: e.target.value })}
                        maxLength={10}
                        placeholder="DNI"
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('countries.documents.codeHint')}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="doc-description">
                        {t('countries.documents.columns.description')}
                      </Label>
                      <Input
                        id="doc-description"
                        value={docForm.description}
                        onChange={(e) => setDocForm({ ...docForm, description: e.target.value })}
                        maxLength={100}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="doc-regex">{t('countries.documents.columns.validator')}</Label>
                      <Input
                        id="doc-regex"
                        value={docForm.validatorRegex}
                        onChange={(e) => setDocForm({ ...docForm, validatorRegex: e.target.value })}
                        maxLength={255}
                        className="font-mono text-xs"
                        placeholder="^\d{7,8}$"
                      />
                      <p className="text-xs text-muted-foreground">{t('countries.regexHint')}</p>
                    </div>
                    <ErrorBanner message={error} />
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setDocDialogOpen(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button type="submit" disabled={saveDoc.isPending || !canSubmitDoc}>
                        {editingDoc ? t('common.save') : t('common.create')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-20">{t('countries.documents.columns.code')}</TableHead>
                  <TableHead>{t('countries.documents.columns.name')}</TableHead>
                  <TableHead className="w-56">
                    {t('countries.documents.columns.description')}
                  </TableHead>
                  <TableHead className="w-64">{t('countries.documents.columns.validator')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((d) => (
                  <TableRow key={d.documentTypeId}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        {d.documentCode}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{d.documentType}</TableCell>
                    <TableCell className="text-muted-foreground">{d.description || '—'}</TableCell>
                    <TableCell
                      className="truncate font-mono text-xs text-muted-foreground"
                      title={d.validatorRegex}
                    >
                      {d.validatorRegex || '—'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={d.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEditDoc(d)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={d.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggleDoc.isPending}
                            onClick={() => toggleDoc.mutate(d)}
                          >
                            {d.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {documents.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 6 : 5}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('countries.documents.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsPanel>

        {/* ---- banks ---- */}
        <TabsPanel value="banks">
          <p className="mb-3 text-sm text-muted-foreground">{t('countries.banks.intro')}</p>

          {canWrite && (
            <div className="mb-3 flex justify-end">
              <Dialog open={bankDialogOpen} onOpenChange={setBankDialogOpen}>
                <DialogTrigger
                  render={
                    <Button size="sm" onClick={openCreateBank}>
                      <Plus />
                      {t('banks.newBank')}
                    </Button>
                  }
                />
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{editingBank ? t('banks.editBank') : t('banks.newBank')}</DialogTitle>
                  </DialogHeader>
                  {/* The country is this page, so the picker is hidden rather
                      than offering a move that belongs on the banks page. */}
                  <BankForm
                    value={bankForm}
                    onChange={setBankForm}
                    onSubmit={() => saveBank.mutate()}
                    onCancel={() => setBankDialogOpen(false)}
                    countries={[]}
                    lockCountry
                    pending={saveBank.isPending}
                    error={error}
                    submitLabel={editingBank ? t('common.save') : t('common.create')}
                  />
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('banks.columns.id')}</TableHead>
                  <TableHead>{t('banks.columns.name')}</TableHead>
                  <TableHead className="w-40">{t('banks.columns.swift')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {banks.map((b) => (
                  <TableRow key={b.bankId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {b.bankId}
                    </TableCell>
                    <TableCell className="font-medium">{b.bank}</TableCell>
                    <TableCell className="font-mono text-xs">{b.swift || '—'}</TableCell>
                    <TableCell>
                      <StatusBadge disabled={b.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEditBank(b)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={b.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggleBank.isPending}
                            onClick={() => toggleBank.mutate(b)}
                          >
                            {b.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {banks.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 5 : 4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('countries.banks.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsPanel>
      </Tabs>
    </div>
  )
}
